/**
 * Chat panel state: whether it is open and which thread it shows. Both are
 * remembered across reloads so a desktop user finds the panel where they
 * left it. On narrower screens the panel is an overlay, so it starts closed.
 */

import { create } from 'zustand'

const KEY = 'job-seeker-chat'

interface Saved {
  open?: boolean
  threadId?: string | null
}

function load(): Saved {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Saved) : {}
  } catch {
    return {}
  }
}

function save(state: Saved): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state))
  } catch {
    // Private mode or a full store: the panel still works for this visit.
  }
}

function wideScreen(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches
}

interface ChatState {
  open: boolean
  threadId: string | null
  setOpen: (open: boolean) => void
  toggle: () => void
  setThreadId: (threadId: string | null) => void
  /** Start a fresh conversation (the thread itself is created on the first message). */
  newThread: () => void
}

const saved = load()

export const useChatStore = create<ChatState>()((set, get) => {
  const persist = (patch: Partial<ChatState>) => {
    set(patch)
    const { open, threadId } = get()
    save({ open, threadId })
  }
  return {
    // First visit on a wide screen: open, so the assistant is seen. After that, where it was left.
    open: Boolean(saved.open) && wideScreen(),
    threadId: saved.threadId ?? null,
    setOpen: (open) => persist({ open }),
    toggle: () => persist({ open: !get().open }),
    setThreadId: (threadId) => persist({ threadId }),
    newThread: () => persist({ threadId: null, open: true }),
  }
})
